import React, { useState, useMemo } from "react";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn, TableAction } from "@shared/types/table";
import { ArrowUp, ArrowDown, AlertTriangle } from "lucide-react";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { PAGE_SIZE } from "@shared/utils/constants";
import { useNavigate } from "react-router-dom";
import { useCustomFilters } from "@features/cx/shared/utils/filters";
import { TransferFilters, TransferRequest, TransferTableItem } from "../../types";
import { useTransferExport, useTransferRequestsWithTab } from "../../hooks";
import { FilterPanel } from "../filters";
// Complaints History List Component
export const HistoryList: React.FC = () => {
  const navigate = useNavigate();
  const remoteUtilityId = useRemoteUtilityId();
  const renderPriority = (value: string) => {
    if (value === "High") {
      return (
        <span
          style={{
            color: "#e53e3e",
            display: "flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          <AlertTriangle size={16} style={{ color: "#e53e3e" }} /> High
        </span>
      );
    }
    if (value === "Medium") {
      return (
        <span
          style={{
            color: "#d97706",
            display: "flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          <ArrowUp size={16} style={{ color: "#d97706" }} /> Medium
        </span>
      );
    }
    if (value === "Low") {
      return (
        <span
          style={{
            color: "#2563eb",
            display: "flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          <ArrowDown size={16} style={{ color: "#2563eb" }} /> Low
        </span>
      );
    }
    if (value === "Critical") {
      return (
        <span
          style={{
            color: "#e53e3e",
            display: "flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          <AlertTriangle size={16} style={{ color: "#e53e3e" }} /> Critical
        </span>
      );
    }
    if (value === "Normal") {
      return (
        <span
          style={{
            color: "#2563eb",
            display: "flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          <ArrowDown size={16} style={{ color: "#2563eb" }} /> Normal
        </span>
      );
    }
    return value;
  };

  const columns: TableColumn<TransferTableItem>[] = [
    { key: "transferId", header: "Transfer ID", type: "text" },
    { key: "customerName", header: "Customer Name", type: "text" },
    { key: "accountNumber", header: "Account", type: "text" },
    { key: "transferType", header: "Transfer Type", type: "text" },
    { key: "requestedDate", header: "Requested Date", type: "text" },
    {
      key: "status",
      header: "Status",
      type: "badge",
      badgeConfig: {
        getVariant: (value: string) => {
          if (value === "pending") return "secondary";
          if (value === "approved") return "default";
          if (value === "completed") return "outline";
          if (value === "rejected") return "destructive";
          if (value === "cancelled") return "destructive";
          return "default";
        },
      },
    },
    { key: "createdUserRemoteName", header: "Created By", type: "text" },
    {
      key: "priority",
      header: "Priority",
      type: "custom",
      render: (value) => renderPriority(value),
    },
  ];

  const filterConfig = useMemo(
    () => ({
      paramNames: {
        status: "status",
        category: "category",
        search: "search_data",
        page: "page",
      },
      defaults: {
        page: 1,
        limit: 50,
        status: [],
        category: "ALL_CATEGORIES",
        search: "",
      },
      parsers: {
        status: (value: string) => value.split(",").map(Number),
        category: (value: string) => value,
        search: (value: string) => value.trim(),
      },
      serializers: {
        status: (value: number[]) => value.join(","),
        category: (value: string) => value,
        search: (value: string) => value.trim(),
      },
    }),
    []
  );

  const {
    filters,
    page,
    handleApplyFilters,
    handleResetFilters,
    handlePageChange,
    handleSearch,
  } = useCustomFilters(filterConfig, Number(remoteUtilityId));

  const exportFilters: TransferFilters = useMemo(() => {
    return {
      remoteUtilityId: Number(remoteUtilityId),
      requestType: "Transfer",
      activeTab: "History",
      searchData: filters.search_data,
      status: filters.status?.length ? filters.status.join(",") : undefined,
      name: filters.name,
      // Note: No page and limit for export to get all data
    };
  }, [filters, remoteUtilityId]);

  const { data } = useTransferRequestsWithTab(
    Number(remoteUtilityId),
    "History",
    {
      page: filters.page,
      limit: PAGE_SIZE,
      searchData: filters.search_data,
      status: filters.status?.length ? filters.status.join(",") : undefined,
      name: filters.name,
    }
  );

  const { exportCSV, isExporting } = useTransferExport(exportFilters);

  const handleExport = () => {
    exportCSV();
  };

  const actions: TableAction<TransferTableItem>[] = [
    {
      label: "View",
      onClick: (item) => navigate(`/cx/transfer/history/${item.id}`),
    },
  ];

  // Transform API data to match the table structure
  const transformedData: TransferTableItem[] = Array.isArray(data?.results)
    ? (data.results as any[]).map((item: any) => ({
        id: item.id?.toString() || "",
        transferId: item.requestNo || "",
        customerName: `${item.firstName || ""} ${item.lastName || ""}`.trim(),
        accountNumber: item.consumerNo || "",
        transferType: item?.utilitySupportRequest?.name,
        requestedDate: item.createdDate || "",
        status: item.statusDisplay || "",
        assignedTo: item.createdUserRemoteName || "",
        financialStatus: "pending", // Default value since it's not in TransferRequest
        priority: item.priorityLevel, // Default value since it's not in TransferRequest
      }))
    : [];

  const totalItems = data?.count || 0;
  const totalPages = Math.ceil(totalItems / PAGE_SIZE);
  const historyTab = 1;

  return (
    <GenericTable
      data={transformedData}
      columns={columns}
      rowKey="transferId"
      searchPlaceholder="Search by name, email, or ID..."
      searchParamName="query"
      searchDebounceMs={300}
      onSearch={handleSearch}
      pagination={{
        currentPage: page,
        totalPages,
        totalItems,
        itemsPerPage: PAGE_SIZE,
        startItem: (page - 1) * PAGE_SIZE + 1,
        endItem: Math.min(page * PAGE_SIZE, totalItems),
      }}
      onPageChange={handlePageChange}
      actions={actions}
      actionsType="dropdown"
      onExport={handleExport}
      exportLoading={isExporting}
      filterPanel={
        <FilterPanel
          currentFilters={filters}
          onApplyFilters={handleApplyFilters}
          onResetFilters={handleResetFilters}
          tab={historyTab}
        />
      }
    />
  );
};
